import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { AlertCircle, ChevronDown, ChevronRight } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { api } from '@/lib/api';
import { AUDIT_EVENT_LABELS } from '@/lib/labels';
import { formatDateTimeISO } from '@/lib/utils';

export const Route = createFileRoute('/audit')({
	component: AuditPage,
});

function AuditPage() {
	const [entityCode, setEntityCode] = useState('');
	const [documentId, setDocumentId] = useState('');
	const [eventType, setEventType] = useState('');
	const [limit, setLimit] = useState(100);
	const [expanded, setExpanded] = useState<Set<string>>(new Set());

	const toggleExpanded = (id: string) =>
		setExpanded((prev) => {
			const next = new Set(prev);
			if (next.has(id)) next.delete(id);
			else next.add(id);
			return next;
		});

	const entitiesQuery = useQuery({ queryKey: ['entities'], queryFn: () => api.entities() });
	const auditQuery = useQuery({
		queryKey: ['audit', { entityCode, documentId, eventType, limit }],
		queryFn: () =>
			api.audit({
				entity_code: entityCode || undefined,
				document_id: documentId || undefined,
				event_type: eventType || undefined,
				limit,
			}),
	});

	return (
		<div className="mx-auto max-w-screen-2xl space-y-6">
			<div>
				<h1 className="text-2xl font-semibold tracking-tight">Audit-trail</h1>
				<p className="text-sm text-muted-foreground">
					Alle events in chronologische volgorde (nieuwste eerst). Filter per entiteit, document of event-type.
				</p>
			</div>

			<Card>
				<CardContent className="grid grid-cols-1 gap-4 p-4 sm:grid-cols-2 lg:grid-cols-4">
					<div className="space-y-1.5">
						<Label>Entiteit</Label>
						<Select value={entityCode} onChange={(e) => setEntityCode(e.target.value)}>
							<option value="">Alle</option>
							{entitiesQuery.data?.entities.map((e) => (
								<option key={e.code} value={e.code}>
									{e.name}
								</option>
							))}
						</Select>
					</div>
					<div className="space-y-1.5">
						<Label>Document-ID</Label>
						<Input
							placeholder="UUID (optioneel)"
							value={documentId}
							onChange={(e) => setDocumentId(e.target.value)}
						/>
					</div>
					<div className="space-y-1.5">
						<Label>Event-type</Label>
						<Select value={eventType} onChange={(e) => setEventType(e.target.value)}>
							<option value="">Alle</option>
							{Object.entries(AUDIT_EVENT_LABELS).map(([value, label]) => (
								<option key={value} value={value}>
									{label}
								</option>
							))}
						</Select>
					</div>
					<div className="space-y-1.5">
						<Label>Limiet</Label>
						<Select value={String(limit)} onChange={(e) => setLimit(Number(e.target.value))}>
							<option value="50">50</option>
							<option value="100">100</option>
							<option value="250">250</option>
							<option value="500">500</option>
						</Select>
					</div>
				</CardContent>
			</Card>

			<Card>
				<CardContent className="p-0">
					<div className="divide-y">
						{auditQuery.isLoading && <div className="p-6 text-sm text-muted-foreground">Laden...</div>}
						{auditQuery.data?.items.length === 0 && (
							<div className="p-6 text-sm text-muted-foreground">Geen events gevonden.</div>
						)}
						{auditQuery.data?.items.map((event) => {
							const isOpen = expanded.has(event.id);
							const hasDetails =
								Boolean(event.error_message) ||
								(event.event_data && typeof event.event_data === 'object');
							return (
								<div key={event.id} className="text-sm">
									<button
										type="button"
										onClick={() => hasDetails && toggleExpanded(event.id)}
										className={`flex w-full items-center gap-3 px-4 py-2 text-left ${
											hasDetails ? 'hover:bg-muted/50' : 'cursor-default'
										}`}
										aria-expanded={isOpen}
									>
										<span className="w-4 shrink-0 text-muted-foreground">
											{hasDetails ? (
												isOpen ? (
													<ChevronDown className="h-4 w-4" />
												) : (
													<ChevronRight className="h-4 w-4" />
												)
											) : null}
										</span>
										<span className="w-40 shrink-0 font-mono text-xs text-muted-foreground">
											{formatDateTimeISO(event.occurred_at)}
										</span>
										<span className="flex-1 truncate font-medium">
											{AUDIT_EVENT_LABELS[event.event_type] ?? event.event_type}
										</span>
										{event.error_message && (
											<AlertCircle className="h-3.5 w-3.5 shrink-0 text-destructive" />
										)}
										{event.document_id && (
											<Link
												to="/documents/$id"
												params={{ id: event.document_id }}
												onClick={(e) => e.stopPropagation()}
												className="shrink-0 font-mono text-xs text-muted-foreground hover:underline"
											>
												doc:{event.document_id.slice(0, 8)}
											</Link>
										)}
									</button>
									{isOpen && hasDetails && (
										<div className="space-y-2 border-t bg-muted/20 px-4 py-3 pl-11">
											{event.error_message && (
												<div className="flex items-start gap-1.5 text-xs text-destructive">
													<AlertCircle className="mt-0.5 h-3 w-3 shrink-0" />
													<span>{event.error_message}</span>
												</div>
											)}
											{event.event_data && typeof event.event_data === 'object' && (
												<pre className="overflow-x-auto rounded-md bg-background p-2 font-mono text-[11px]">
													{JSON.stringify(event.event_data, null, 2)}
												</pre>
											)}
										</div>
									)}
								</div>
							);
						})}
					</div>
				</CardContent>
			</Card>
			{auditQuery.data && auditQuery.data.items.length >= limit && (
				<div className="text-center text-xs text-muted-foreground">
					Resultaten beperkt tot {limit}. Verhoog limiet via de filter.
				</div>
			)}
			<div className="text-center">
				<Button variant="ghost" size="sm" asChild>
					<Link to="/">Terug naar dashboard</Link>
				</Button>
			</div>
		</div>
	);
}
